import type { FetchOptions, FetchRequest } from 'ofetch'
import { ofetch } from 'ofetch'
import { useAuthStore } from '@/features/auth/model'
import { authPersistence } from '@/shared/lib/storage'
import { handleApiError } from './api.error'
import { getCurrentApiBaseUrl } from './api.lib'
import { isAuthenticated } from './api.token'

const NO_RELOAD_ON_AUTH_ERROR_PATHS = [
  '/me',
  '/credentials/register',
  '/v2/credentials/sso/register',
]

function isNoReloadEndpoint(url: string) {
  return NO_RELOAD_ON_AUTH_ERROR_PATHS.some(path =>
    url.endsWith(path) || url.includes(`${path}?`),
  )
}

/**
 * Creates a configured fetch instance for API requests.
 * This instance includes authentication headers, error handling, and request/response interceptors.
 */
const _apiFetchInstance = ofetch.create({
  headers: {
    Accept: 'application/json',
  },

  // Interceptors
  onRequest: async ({ options }) => {
    const token = authPersistence.getAuthToken()

    if (!isAuthenticated() && token) {
      // Clear both stores
      authPersistence.clearAllData()
      useAuthStore.getState().logout()
    }

    const headers = new Headers(options.headers || {})

    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json')
      }
    }

    options.headers = headers
  },

  async onRequestError({ request, error, options }) {
    console.error(`[API Request Error] ${options.method} ${request}`, error)
  },

  async onResponseError({ response }) {
    if (response.status === 401 || response.status === 403) {
      // Use whitelist array to determine if reload should be skipped
      const shouldNotReload = isNoReloadEndpoint(response.url)

      if (shouldNotReload) {
      // Do not reload for these endpoints, just throw the error
        throw handleApiError(response)
      }

      // Check if we had a token before clearing auth data
      const authState = useAuthStore.getState()
      const hadToken = authState.token !== null || authPersistence.getAuthToken() !== null

      // Clear auth data from persistence layer
      authPersistence.clearAllData()
      useAuthStore.getState().logout()

      // Only force refresh if we previously had a token (user was authenticated)
      // and auth is ready (not in the middle of SSO redirect)
      if (hadToken && typeof window !== 'undefined') {
        console.warn('Previously authenticated, reloading page')
        window.location.reload()
      }

      throw handleApiError(response)
    }
    // Attach the parsed response body so callers can read errorType / errorDescription.
    const err = handleApiError(response) as any
    err.data = (response as any)._data
    throw err
  },
})

type ApiFetchOptions = Omit<FetchOptions<'json'>, 'baseURL'>

/**
 * Makes an API request
 * @returns The response from the API
 */
export async function apiFetch<T = unknown>(
  endpoint: FetchRequest,
  options: ApiFetchOptions = {},
): Promise<T> {
  const baseUrl = getCurrentApiBaseUrl()
  // Ensure endpoint is treated as a path segment relative to the base URL
  const path = String(endpoint).startsWith('/') ? String(endpoint) : `/${endpoint}`
  const requestUrl = `${baseUrl}${path}`

  try {
    return await _apiFetchInstance<T>(requestUrl, options)
  } catch (error) {
    const apiError = handleApiError(error) as any
    if ((error as any)?.data !== undefined) {
      apiError.data = (error as any).data
    }
    throw apiError
  }
}
